// ReasonCell — THE short-reason cell: the first clause of a long explanation in
// the row, the whole text on hover (Alex, 2026-10-11: "Can the reason text be
// shorter. Is not clear, can we add something that gives more feedback when
// hovering? Ditto, if we do that, must be a new standard improvement. Not one
// of a kind."). Any grid column that holds a sentence explaining a row — a sync
// reason, a not-applicable detail, a finding — renders through this, not its
// own truncation.
//
// What counts as SHORT is decided in src/web/short-reason.js (pinned by a test).
//
// The full text opens in a PORTAL on document.body — the same escape-the-cell's-
// overflow trick HoverZoomImg uses, with the same short close delay so crossing
// into the popover doesn't flicker it shut. Only a shortened cell gets one; a
// cell that already shows everything has nothing more to say.
//
// Colour-blind safe (RULES 3): "more on hover" is carried by SHAPE — a trailing
// "…" and a dotted underline — never by colour.
//
//   text — the full sentence (null/empty renders "—")
//   max  — optional cap for the short form (default 60)

import { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { shortReason, REASON_MAX as MAX } from '../short-reason.js';

export default function ReasonCell({ text, max = MAX }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  const open = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ left: r.left, top: r.bottom + 4 });
  }, []);
  const scheduleClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPos(null), 150);
  }, []);
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  const { short, cut } = shortReason(text, max);
  if (!short) return <span style={{ color: 'var(--th-text-disabled)' }}>—</span>;
  if (!cut) return <span>{short}</span>;

  return (
    <>
      <span
        ref={ref}
        tabIndex={0}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={scheduleClose}
        aria-label={String(text)}
        style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3, cursor: 'help' }}
      >{short}…</span>
      {pos && createPortal(
        <div
          role="tooltip"
          onMouseEnter={open}
          onMouseLeave={scheduleClose}
          style={{
            position: 'fixed', left: pos.left, top: pos.top, zIndex: 400, maxWidth: 420,
            background: 'var(--th-row-even)', color: 'var(--th-text-body)',
            border: '1px solid var(--th-border-subtle)', borderRadius: 4,
            padding: '6px 8px', boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            fontSize: 'var(--fs-small)', lineHeight: 1.4, whiteSpace: 'normal',
          }}
        >{String(text)}</div>,
        document.body
      )}
    </>
  );
}
